import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import OolaWheelMilestoneV2 from '../OolaWheelMilestoneV2';
import ModalConfirmV2 from '../../Modals/ModalConfirmV2';
import { readableFColor } from '../../../../util/readableBrandColor';
import { FLineIcon } from '../FLineIcons';
import './PickSevenV2.css';

function formatDate(value) {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

function formatDateShort(value) {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
    });
}

function goalKey(areaId, indexF, item) {
    return item?.idL || item?.id || `${areaId}${indexF}`;
}

/**
 * Portal V2 — Pick Top 7 goals.
 * Wheel navigates Fs; one F at a time; sticky Top 7 tray.
 * Classic OolaSevenSection / SelectSevenSection stay on /steps.
 */
export default function PickSevenV2({
    AllState,
    changeData,
    confirmGoals,
}) {
    const [focusF, setFocusF] = useState(0);
    const [showConfirm, setShowConfirm] = useState(false);
    const [flashMsg, setFlashMsg] = useState('');
    const [slideDir, setSlideDir] = useState(0);
    const navRef = useRef(null);
    const trayRef = useRef(null);
    const flashTimer = useRef(null);
    const swipeRef = useRef({ x: null, y: null });
    const didResetToFitness = useRef(false);

    const seven = AllState.SelectedGoalsSeven || [];
    const group = AllState.SelectedGoalsGroup || {};
    const count = seven.length;
    const ready = count === 7;

    const active = AllState.Fs?.[focusF] || {};
    const accent = readableFColor(active.name, active.color || '#15c285');
    const areaGoals = group[focusF]?.selected || [];
    const areaScore =
        active.score != null && !Number.isNaN(Number(active.score))
            ? Number(active.score).toFixed(1)
            : null;

    const pickedKeys = useMemo(() => {
        const set = new Set();
        seven.forEach((g) => {
            if (g.idL) set.add(g.idL);
            if (g.id) set.add(g.id);
            if (g.GoalId != null) set.add(`gid-${g.GoalId}`);
        });
        return set;
    }, [seven]);

    const countsByF = useMemo(() => {
        const c = [0, 0, 0, 0, 0, 0, 0];
        seven.forEach((g) => {
            const a = Number(g.areaId);
            if (a >= 0 && a <= 6) c[a] += 1;
        });
        return c;
    }, [seven]);

    useEffect(() => {
        const rail = navRef.current;
        if (!rail) return;
        const activeBtn = rail.querySelector('.pv2-pick7__f.is-active');
        if (!activeBtn || typeof activeBtn.scrollIntoView !== 'function') return;
        activeBtn.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest',
        });
    }, [focusF]);

    /* Always open Pick Top 7 on Fitness (index 0), mobile + desktop */
    useEffect(() => {
        if (didResetToFitness.current) return;
        didResetToFitness.current = true;
        setFocusF(0);
        if (Number(AllState.CurrentF) !== 0) {
            changeData({ ...AllState, CurrentF: 0 });
        }
        // intentionally once on enter
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Mobile: after 4 picks, keep scrolling the slot tray so empty slots stay visible */
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (window.matchMedia('(max-width: 767px)').matches === false) return;
        if (count < 4) return;
        const tray = trayRef.current;
        if (!tray) return;
        const targetIndex = Math.min(count, 6);
        const slot = tray.children?.[targetIndex];
        if (!slot || typeof slot.scrollIntoView !== 'function') return;
        slot.scrollIntoView({
            behavior: 'smooth',
            inline: 'end',
            block: 'nearest',
        });
    }, [count]);

    useEffect(
        () => () => {
            if (flashTimer.current) window.clearTimeout(flashTimer.current);
        },
        []
    );

    const showFlash = (msg) => {
        if (flashTimer.current) window.clearTimeout(flashTimer.current);
        setFlashMsg(msg);
        flashTimer.current = window.setTimeout(() => setFlashMsg(''), 1600);
    };

    const switchF = (index) => {
        if (index < 0 || index > 6) return;
        if (index === focusF) return;
        setSlideDir(index > focusF ? 1 : -1);
        setFocusF(index);
        changeData({ ...AllState, CurrentF: index });
    };

    const onFocusTouchStart = (event) => {
        const touch = event.changedTouches?.[0];
        if (!touch) return;
        swipeRef.current = { x: touch.clientX, y: touch.clientY };
    };

    const onFocusTouchEnd = (event) => {
        const start = swipeRef.current;
        if (start.x == null || start.y == null) return;
        const touch = event.changedTouches?.[0];
        swipeRef.current = { x: null, y: null };
        if (!touch) return;
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        if (Math.abs(dx) < 56) return;
        if (Math.abs(dx) < Math.abs(dy) * 1.15) return;
        if (dx < 0) switchF(focusF + 1);
        else switchF(focusF - 1);
    };

    const isItemSelected = (areaId, indexF, item) => {
        if (item?.selected) return true;
        const key = goalKey(areaId, indexF, item);
        if (pickedKeys.has(key)) return true;
        if (item?.GoalId != null && pickedKeys.has(`gid-${item.GoalId}`)) {
            return true;
        }
        return false;
    };

    const toggleGoal = (areaId, indexF) => {
        const area = group[areaId];
        if (!area?.selected?.[indexF]) return;

        const item = area.selected[indexF];
        const key = goalKey(areaId, indexF, item);
        const selected = isItemSelected(areaId, indexF, item);

        if (!selected && count >= 7) {
            setShowConfirm(true);
            return;
        }

        const nextGroup = { ...group };
        const nextSelected = area.selected.map((g, i) =>
            i === indexF ? { ...g } : g
        );
        nextGroup[areaId] = { ...area, selected: nextSelected };

        let nextSeven = [...seven];

        if (!selected) {
            nextSelected[indexF].selected = true;
            nextSelected[indexF].idL = key;
            nextSelected[indexF].id = key;
            nextSeven.push({ ...nextSelected[indexF] });
            if (nextSeven.length >= 7) {
                showFlash('Your Top 7 is ready.');
            } else {
                showFlash('Added to your Top 7');
            }
        } else {
            nextSelected[indexF].selected = false;
            nextSeven = nextSeven.filter((g) => {
                const gKey = g.idL || g.id;
                if (gKey && gKey === key) return false;
                if (
                    item.GoalId != null &&
                    g.GoalId != null &&
                    g.GoalId === item.GoalId
                ) {
                    return false;
                }
                return true;
            });
        }

        changeData({
            ...AllState,
            CurrentF: focusF,
            SelectedGoalsGroup: nextGroup,
            SelectedGoalsSeven: nextSeven,
        });
    };

    const removeFromTray = (goal) => {
        const key = goal.idL || goal.id;
        const nextGroup = { ...group };
        Object.keys(nextGroup).forEach((areaKey) => {
            const area = nextGroup[areaKey];
            if (!area?.selected) return;
            nextGroup[areaKey] = {
                ...area,
                selected: area.selected.map((g) => {
                    const gKey = g.idL || g.id;
                    const match =
                        (key && gKey === key) ||
                        (goal.GoalId != null &&
                            g.GoalId != null &&
                            g.GoalId === goal.GoalId);
                    return match ? { ...g, selected: false } : g;
                }),
            };
        });
        const nextSeven = seven.filter((g) => {
            const gKey = g.idL || g.id;
            if (key && gKey === key) return false;
            if (
                goal.GoalId != null &&
                g.GoalId != null &&
                g.GoalId === goal.GoalId
            ) {
                return false;
            }
            return true;
        });
        changeData({
            ...AllState,
            SelectedGoalsGroup: nextGroup,
            SelectedGoalsSeven: nextSeven,
        });
    };

    const slots = Array.from({ length: 7 }, (_, i) => seven[i] || null);
    const areaPicked = areaGoals.filter((g, i) =>
        isItemSelected(focusF, i, g)
    ).length;
    const prevF = focusF > 0 ? AllState.Fs?.[focusF - 1] : null;
    const nextF = focusF < 6 ? AllState.Fs?.[focusF + 1] : null;
    const prevAccent = prevF
        ? readableFColor(prevF.name, prevF.color || '#15c285')
        : undefined;
    const nextAccent = nextF
        ? readableFColor(nextF.name, nextF.color || '#15c285')
        : undefined;

    return (
        <section
            className={`pv2-pick7${ready ? ' is-complete' : ''}`}
        >
            <aside
                className="pv2-pick7__side"
                style={{ ['--pv2-pick7-side-accent']: accent }}
            >
                <div className="pv2-pick7__wheel">
                    <OolaWheelMilestoneV2
                        Fs={AllState.Fs || []}
                        activeIndex={focusF}
                        nextName={AllState.Fs?.[focusF + 1]?.name}
                        nextColor={AllState.Fs?.[focusF + 1]?.color}
                        focusScore={areaScore}
                        goalProgress
                        lastReachedIndex={6}
                        onSelectF={switchF}
                    />
                </div>
                <div className="pv2-pick7__focus-pill" aria-live="polite">
                    <span className="pv2-pick7__focus-pill-label">
                        {active.name || 'Area'} selected
                    </span>
                    <span className="pv2-pick7__focus-pill-meta">
                        {countsByF[focusF]} of {areaGoals.length || 0} picked
                    </span>
                </div>
                <p className="pv2-pick7__wheel-hint">
                    Click an F on the wheel or use the arrows to choose its
                    goals
                </p>
                <p className="pv2-pick7__side-note">
                    {ready
                        ? '7 selected · Ready to arrange'
                        : 'Choose the goals that feel most important right now.'}
                </p>
            </aside>

            <div
                className="pv2-pick7__panel"
                style={{ ['--pv2-pick7-accent']: accent }}
            >
                <nav
                    className="pv2-pick7__fnav"
                    aria-label="Life areas. Tap to switch, or swipe the goals list."
                    ref={navRef}
                >
                    {(AllState.Fs || []).map((f, i) => {
                        const fAccent = readableFColor(
                            f.name,
                            f.color || '#15c285'
                        );
                        const done = countsByF[i] > 0;
                        const isActive = i === focusF;
                        return (
                            <button
                                key={f.areaId || f.name || i}
                                type="button"
                                className={`pv2-pick7__f${
                                    isActive ? ' is-active' : ''
                                }${done ? ' is-done' : ''}`}
                                style={{ ['--f-accent']: fAccent }}
                                aria-current={isActive ? 'true' : undefined}
                                aria-label={`${f.name || 'Area'}${
                                    done ? `, ${countsByF[i]} picked` : ''
                                }`}
                                onClick={() => switchF(i)}
                            >
                                <span
                                    className="pv2-pick7__f-ico"
                                    aria-hidden="true"
                                >
                                    <FLineIcon name={f.name} index={i} />
                                </span>
                                <span className="pv2-pick7__f-name">
                                    {f.name}
                                </span>
                                {done ? (
                                    <span
                                        className="pv2-pick7__f-check"
                                        aria-hidden="true"
                                    >
                                        ✓
                                    </span>
                                ) : null}
                            </button>
                        );
                    })}
                </nav>

                <header className="pv2-pick7__head">
                    <div className="pv2-pick7__titles">
                        <p className="pv2-pick7__eyebrow">Step 2 · Top 7</p>
                        <h2 className="pv2-pick7__title">Pick your Top 7</h2>
                        <p className="pv2-pick7__lead">
                            Choose the seven goals that bring more balance and
                            feel closest to your purpose.
                        </p>
                        <p className="pv2-pick7__lead pv2-pick7__lead--mobile">
                            Choose the 7 goals that matter most right now.
                        </p>
                    </div>
                    <div
                        className={`pv2-pick7__meter${
                            ready ? ' is-ready' : ''
                        }`}
                        aria-label={`${count} of 7 goals selected`}
                    >
                        <span className="pv2-pick7__meter-kicker">
                            Selected
                        </span>
                        <strong>
                            {count}
                            <em> / 7</em>
                        </strong>
                        <div
                            className="pv2-pick7__meter-bar"
                            aria-hidden="true"
                        >
                            <span style={{ width: `${(count / 7) * 100}%` }} />
                        </div>
                        <span className="pv2-pick7__meter-note">
                            {ready
                                ? 'Your Top 7 is ready.'
                                : 'Choose the 7 goals that matter most right now.'}
                        </span>
                    </div>
                </header>

                <p
                    className={`pv2-pick7__flash${flashMsg ? ' is-on' : ''}`}
                    aria-live="polite"
                >
                    {flashMsg || '\u00a0'}
                </p>

                <div
                    className="pv2-pick7__tray"
                    aria-label="Your Top 7 slots"
                    ref={trayRef}
                >
                    {slots.map((goal, i) => {
                        const f =
                            goal != null
                                ? AllState.Fs?.[goal.areaId] || {}
                                : null;
                        const c = f
                            ? readableFColor(f.name, f.color || '#15c285')
                            : 'rgba(255,255,255,0.22)';
                        return (
                            <button
                                key={`slot-${i}`}
                                type="button"
                                className={`pv2-pick7__slot${
                                    goal ? ' is-filled' : ''
                                }`}
                                style={{ ['--slot-c']: c }}
                                disabled={!goal}
                                onClick={() => goal && removeFromTray(goal)}
                                title={
                                    goal
                                        ? `${goal.text} (click to remove)`
                                        : `Slot ${i + 1} · open`
                                }
                                aria-label={
                                    goal
                                        ? `Selected goal ${i + 1}: ${
                                              goal.text
                                          }. Tap to remove.`
                                        : `Empty Top 7 slot ${i + 1}`
                                }
                            >
                                <span className="pv2-pick7__slot-num">
                                    {i + 1}
                                </span>
                                {goal ? (
                                    <>
                                        <span className="pv2-pick7__slot-area">
                                            {f?.name || 'Goal'}
                                        </span>
                                        <span className="pv2-pick7__slot-text">
                                            {goal.text}
                                        </span>
                                    </>
                                ) : (
                                    <span className="pv2-pick7__slot-empty">
                                        Open
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div
                    className="pv2-pick7__focus"
                    onTouchStart={onFocusTouchStart}
                    onTouchEnd={onFocusTouchEnd}
                >
                    <div className="pv2-pick7__focus-head">
                        <div className="pv2-pick7__focus-title">
                            <h3 style={{ color: accent }}>
                                {active.name || 'Area'} goals
                            </h3>
                            <p className="pv2-pick7__focus-hint">
                                Tap an F above or swipe
                            </p>
                        </div>
                        <span className="pv2-pick7__focus-count">
                            <span className="pv2-pick7__focus-count-mobile">
                                {areaPicked} of {areaGoals.length} selected
                            </span>
                            <span className="pv2-pick7__focus-count-desk">
                                {areaPicked} selected in{' '}
                                {active.name || 'this area'}
                            </span>
                        </span>
                    </div>

                    <div
                        key={focusF}
                        className={`pv2-pick7__list${
                            slideDir < 0
                                ? ' is-from-left'
                                : slideDir > 0
                                  ? ' is-from-right'
                                  : ''
                        }`}
                    >
                        {areaGoals.length === 0 ? (
                            <p className="pv2-pick7__empty">
                                No goals in this area yet. Go back and set 3
                                goals first.
                            </p>
                        ) : (
                            areaGoals.map((item, indexF) => {
                                const selected = isItemSelected(
                                    focusF,
                                    indexF,
                                    item
                                );
                                return (
                                    <button
                                        key={goalKey(focusF, indexF, item)}
                                        type="button"
                                        className={`pv2-pick7__goal${
                                            selected ? ' is-on' : ''
                                        }`}
                                        onClick={() =>
                                            toggleGoal(focusF, indexF)
                                        }
                                        aria-pressed={selected}
                                    >
                                        <span
                                            className="pv2-pick7__goal-check"
                                            aria-hidden="true"
                                        >
                                            {selected ? '✓' : ''}
                                        </span>
                                        <span className="pv2-pick7__goal-body">
                                            <span className="pv2-pick7__goal-num">
                                                {String(indexF + 1).padStart(
                                                    2,
                                                    '0'
                                                )}
                                            </span>
                                            <span className="pv2-pick7__goal-text">
                                                {item.text}
                                            </span>
                                            {item.date ? (
                                                <span className="pv2-pick7__goal-date">
                                                    <span className="pv2-pick7__goal-date-full">
                                                        {formatDate(item.date)}
                                                    </span>
                                                    <span className="pv2-pick7__goal-date-short">
                                                        {formatDateShort(
                                                            item.date
                                                        )}
                                                    </span>
                                                </span>
                                            ) : null}
                                        </span>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>

                <div className="pv2-pick7__actions">
                    <div className="pv2-pick7__nav-fs">
                        <button
                            type="button"
                            className="pv2-pick7__nav-btn pv2-pick7__nav-btn--prev"
                            style={
                                prevAccent
                                    ? { ['--nav-f']: prevAccent }
                                    : undefined
                            }
                            disabled={!prevF}
                            onClick={() => switchF(focusF - 1)}
                            aria-label={
                                prevF
                                    ? `Previous area: ${prevF.name}`
                                    : 'No previous area'
                            }
                        >
                            <span className="pv2-pick7__nav-arrow" aria-hidden="true">
                                ←
                            </span>
                            <span className="pv2-pick7__nav-name">
                                {prevF?.name || 'Prev'}
                            </span>
                        </button>
                        <button
                            type="button"
                            className="pv2-pick7__nav-btn pv2-pick7__nav-btn--next"
                            style={
                                nextAccent
                                    ? { ['--nav-f']: nextAccent }
                                    : undefined
                            }
                            disabled={!nextF}
                            onClick={() => switchF(focusF + 1)}
                            aria-label={
                                nextF
                                    ? `Next area: ${nextF.name}`
                                    : 'No next area'
                            }
                        >
                            <span className="pv2-pick7__nav-name">
                                {nextF?.name || 'Next'}
                            </span>
                            <span className="pv2-pick7__nav-arrow" aria-hidden="true">
                                →
                            </span>
                        </button>
                    </div>
                    <button
                        type="button"
                        className={`pv2-pick7__cta${
                            ready ? ' is-ready' : ' is-disabled'
                        }`}
                        aria-disabled={!ready}
                        onClick={() => {
                            if (!ready) return;
                            setShowConfirm(true);
                        }}
                    >
                        {ready
                            ? 'Continue to Arrange →'
                            : 'Select 7 goals to continue'}
                    </button>
                </div>
            </div>

            {typeof document !== 'undefined'
                ? createPortal(
                      <div
                          className={`pv2-pick7__dock${
                              ready ? ' is-complete' : ''
                          }`}
                          aria-label="Selection progress"
                      >
                          <div className="pv2-pick7__dock-meta">
                              <span>{`${count} / 7 selected`}</span>
                          </div>
                          <button
                              type="button"
                              className={`pv2-pick7__dock-cta${
                                  ready ? ' is-ready' : ' is-disabled'
                              }`}
                              disabled={!ready}
                              aria-disabled={!ready}
                              onClick={() => {
                                  if (!ready) return;
                                  setShowConfirm(true);
                              }}
                          >
                              {ready
                                  ? 'Continue to Arrange →'
                                  : 'Select 7 goals to continue'}
                          </button>
                      </div>,
                      document.body
                  )
                : null}

            <ModalConfirmV2
                show={showConfirm}
                onClose={() => setShowConfirm(false)}
                title={
                    ready
                        ? 'Your Top 7 are set. Ready to continue?'
                        : 'You already have 7 goals'
                }
                description={
                    ready
                        ? 'These become the goals you’ll refine next. You can still edit before continuing.'
                        : 'Deselect one from your tray to pick a different goal, or continue with your Top 7.'
                }
                cancelLabel={ready ? 'Keep editing' : 'Change picks'}
                confirmLabel="Yes, continue"
                onConfirm={() => {
                    if (ready) confirmGoals();
                    else setShowConfirm(false);
                }}
            />
        </section>
    );
}
